import React, { useState } from "react";
import { API } from "../../App";
import { toast } from "sonner";
import axios from "axios";
import { Sparkles, Copy, Facebook, Globe, Megaphone } from "lucide-react";

const GhostAdMakerTab = () => {
  const [form, setForm] = useState({ service: "", location: "", offer: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleGenerate = async () => {
    if (!form.service) { toast.error("Service is required"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ghost-ad-maker`, form, { withCredentials: true });
      setResult(res.data.ads);
      toast.success("Ads generated!");
    } catch (e) { toast.error("Failed to generate ads"); }
    finally { setLoading(false); }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const services = [
    "Interior Painting", "Exterior Painting", "Drywall Repair", "Flooring Installation",
    "Tile Work", "Cabinet Installation", "Roof Repair", "Bathroom Renovation",
    "Kitchen Renovation", "General Handyman", "Stucco Work", "Plumbing Repair"
  ];

  return (
    <div className="space-y-6" data-testid="ghost-ad-maker-tab">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#4A2B6E]/30 via-[#0D5C63]/20 to-[#FF6B35]/20 border border-[#2A2B32] rounded-xl p-6">
        <div className="flex items-center space-x-3 mb-2">
          <Sparkles size={24} className="text-[#FF6B35]" />
          <h2 className="text-xl font-bold text-white">Ghost Ad Maker</h2>
        </div>
        <p className="text-[#A0AAB2] text-sm">Generate professional ad copy for Facebook, Google, and social media in seconds.</p>
        <p className="text-[#FF6B35] text-xs mt-1 font-medium">Powered by Prestigious Companies</p>
      </div>

      {/* Input Form */}
      <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
        <div className="space-y-4">
          <div>
            <label className="text-[#A0AAB2] text-sm mb-1 block">Service *</label>
            <select value={form.service} onChange={e => setForm(p => ({...p, service: e.target.value}))}
              className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" data-testid="ad-service">
              <option value="">Select service</option>
              {services.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[#A0AAB2] text-sm mb-1 block">Location</label>
            <input type="text" value={form.location} onChange={e => setForm(p => ({...p, location: e.target.value}))}
              className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" placeholder="e.g. Lakeland, FL" data-testid="ad-location" />
          </div>
          <div>
            <label className="text-[#A0AAB2] text-sm mb-1 block">Special Offer</label>
            <input type="text" value={form.offer} onChange={e => setForm(p => ({...p, offer: e.target.value}))}
              className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white" placeholder="e.g. 15% off first project" data-testid="ad-offer" />
          </div>
          <button onClick={handleGenerate} disabled={loading} data-testid="generate-ad-btn"
            className="w-full gradient-btn py-3 rounded-lg font-semibold text-lg flex items-center justify-center space-x-2 disabled:opacity-50">
            <Sparkles size={20} />
            <span>{loading ? "Generating..." : "Generate My Ad"}</span>
          </button>
        </div>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Facebook Ad */}
          {result.facebook_ad && (
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Facebook size={18} className="text-blue-400" />
                  <h3 className="text-white font-semibold">Facebook Ad</h3>
                </div>
                <button onClick={() => copyText(`${result.facebook_ad.headline}\n\n${result.facebook_ad.body}\n\n${result.facebook_ad.cta}`)}
                  className="p-2 bg-[#2A2B32] text-[#A0AAB2] rounded-lg hover:text-white" data-testid="copy-fb-ad">
                  <Copy size={14} />
                </button>
              </div>
              <div className="bg-[#1F2027] rounded-lg p-4 space-y-2">
                <p className="text-[#FF6B35] font-bold text-lg">{result.facebook_ad.headline}</p>
                <p className="text-white text-sm">{result.facebook_ad.body}</p>
                <div className="inline-block bg-[#FF6B35] text-white px-4 py-2 rounded-lg text-sm font-semibold mt-2">{result.facebook_ad.cta}</div>
              </div>
            </div>
          )}

          {/* Google Ad */}
          {result.google_ad && (
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Globe size={18} className="text-green-400" />
                  <h3 className="text-white font-semibold">Google Ad</h3>
                </div>
                <button onClick={() => copyText(`${result.google_ad.headline_1}\n${result.google_ad.headline_2}\n${result.google_ad.description}`)}
                  className="p-2 bg-[#2A2B32] text-[#A0AAB2] rounded-lg hover:text-white" data-testid="copy-google-ad">
                  <Copy size={14} />
                </button>
              </div>
              <div className="bg-[#1F2027] rounded-lg p-4 space-y-1">
                <p className="text-blue-400 font-semibold">{result.google_ad.headline_1}</p>
                <p className="text-blue-400 font-semibold">{result.google_ad.headline_2}</p>
                <p className="text-white text-sm mt-1">{result.google_ad.description}</p>
              </div>
            </div>
          )}

          {/* Quick Promo */}
          {result.quick_promo && (
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Megaphone size={18} className="text-[#FF6B35]" />
                  <h3 className="text-white font-semibold">Quick Promo</h3>
                </div>
                <button onClick={() => copyText(`${result.quick_promo.text}\n\n${result.quick_promo.hashtags}`)}
                  className="p-2 bg-[#2A2B32] text-[#A0AAB2] rounded-lg hover:text-white" data-testid="copy-promo">
                  <Copy size={14} />
                </button>
              </div>
              <div className="bg-[#1F2027] rounded-lg p-4">
                <p className="text-white text-sm mb-2">{result.quick_promo.text}</p>
                <p className="text-[#FF6B35] text-xs">{result.quick_promo.hashtags}</p>
              </div>
            </div>
          )}

          {/* Raw text fallback */}
          {result.raw_text && (
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-semibold">Generated Ad Copy</h3>
                <button onClick={() => copyText(result.raw_text)}
                  className="p-2 bg-[#2A2B32] text-[#A0AAB2] rounded-lg hover:text-white">
                  <Copy size={14} />
                </button>
              </div>
              <pre className="text-white text-sm whitespace-pre-wrap bg-[#1F2027] rounded-lg p-4">{result.raw_text}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GhostAdMakerTab;
